import type { z } from "zod";

import {
  dashboardChannelEnvironmentSchema,
  dashboardLaunchChannelEnvironmentPreviewResponseSchema,
  dashboardListChannelEnvironmentProfilesResponseSchema,
  dashboardPatchChannelEnvironmentResponseSchema,
  dashboardPreviewPublicConfigReasonSchema,
} from "@arti/schemas/dashboard";
import {
  nodeChannelEnvironmentLifecycleErrorResponseSchema,
  nodeChannelEnvironmentResumeUnavailableResponseSchema,
} from "@arti/schemas/node";
import { assertUnreachable, hasValue, isRecord } from "@arti/utilities";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useDashboardRealtime } from "../../hooks/useDashboardRealtime";

/**
 * Canonical channel-environment shape, inferred from the same schema the server
 * validates its response against (`@arti/schemas/dashboard`). Do NOT hand-roll a
 * parallel parser here — that drifted once already (egressPolicy went string →
 * structured object and the manual reader kept throwing "Invalid egressPolicy",
 * nulling the env and faking a "nothing attached" state). Parsing through the
 * shared schema makes that class of drift impossible.
 */
export type ChannelEnvironment = z.infer<
  typeof dashboardChannelEnvironmentSchema
>;

/** Mirrors the server's isFailedUnstartedEnvironment: an environment that
 * errored before ever getting a container. */
export function isFailedUnstartedEnvironment(
  environment: ChannelEnvironment,
): boolean {
  return (
    environment.containerId === null &&
    (environment.status === "errored" ||
      (environment.status === "archived" && environment.lastErrorAt !== null))
  );
}

export type ChannelEnvironmentPatchResponse = z.infer<
  typeof dashboardPatchChannelEnvironmentResponseSchema
>;

export type PreviewPublicConfigReason = z.infer<
  typeof dashboardPreviewPublicConfigReasonSchema
>;

export type ChannelEnvironmentProfile = z.infer<
  typeof dashboardListChannelEnvironmentProfilesResponseSchema
>["profiles"][number];

export interface ChannelEnvironmentFormValues {
  actorProcessToolsEnabled: boolean;
  cpuCount: number;
  idleStopAfterHours: number | null;
  maxConcurrentSpawns: number;
  maxSpawnWallSeconds: number | null;
  previewPorts: ChannelEnvironment["previewPorts"];
  profileId?: string | null;
  ramMB: number;
  retentionDaysAfterChannelKill: number | null;
  storageMB: number;
}

interface ChannelEnvironmentState {
  environment: ChannelEnvironment | null;
  error: string | null;
  loading: boolean;
}

interface ChannelEnvironmentProfilesState {
  error: string | null;
  loading: boolean;
  profiles: ChannelEnvironmentProfile[];
}

const DEFAULT_REFRESH_MS = 10_000;

export const DEFAULT_CHANNEL_ENVIRONMENT_FORM: ChannelEnvironmentFormValues = {
  actorProcessToolsEnabled: true,
  cpuCount: 1,
  idleStopAfterHours: 168,
  maxConcurrentSpawns: 5,
  maxSpawnWallSeconds: 86_400,
  previewPorts: [],
  ramMB: 2048,
  retentionDaysAfterChannelKill: 30,
  storageMB: 2048,
};

export function useChannelEnvironment(channelId: string | null): {
  archive: () => Promise<void>;
  attach: (values: ChannelEnvironmentFormValues) => Promise<void>;
  detach: () => Promise<void>;
  environment: ChannelEnvironment | null;
  error: string | null;
  launchPreview: (publicPort: number) => Promise<string>;
  loading: boolean;
  patch: (
    values: ChannelEnvironmentFormValues,
    options?: { confirmRestart?: boolean },
  ) => Promise<ChannelEnvironmentPatchResponse>;
  profiles: ChannelEnvironmentProfile[];
  profilesError: string | null;
  profilesLoading: boolean;
  refresh: () => Promise<void>;
  refreshProfiles: () => Promise<void>;
  restart: () => Promise<void>;
  start: () => Promise<void>;
} {
  const [state, setState] = useState<ChannelEnvironmentState>({
    environment: null,
    error: null,
    loading: Boolean(channelId),
  });
  const [profilesState, setProfilesState] =
    useState<ChannelEnvironmentProfilesState>({
      error: null,
      loading: true,
      profiles: [],
    });
  const { subscribe } = useDashboardRealtime();

  const refreshProfiles = useCallback(async () => {
    setProfilesState((previous) => ({ ...previous, loading: true }));
    try {
      const response = await fetch("/user/channel-environment-profiles", {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const parsed =
        dashboardListChannelEnvironmentProfilesResponseSchema.parse(
          await response.json(),
        );
      setProfilesState({
        error: null,
        loading: false,
        profiles: parsed.profiles,
      });
    } catch (error) {
      setProfilesState({
        error:
          error instanceof Error ? error.message : "Failed to load profiles",
        loading: false,
        profiles: [],
      });
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!channelId) {
      setState({ environment: null, error: null, loading: false });
      return;
    }
    setState((previous) => ({ ...previous, loading: true }));
    try {
      const response = await fetch(`/user/channels/${channelId}/environment`, {
        credentials: "include",
      });
      if (response.status === 404) {
        setState({ environment: null, error: null, loading: false });
        return;
      }
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const environment = dashboardChannelEnvironmentSchema.parse(
        await response.json(),
      );
      setState({ environment, error: null, loading: false });
    } catch (error) {
      setState({
        environment: null,
        error: error instanceof Error ? error.message : "Failed to load env",
        loading: false,
      });
    }
  }, [channelId]);

  useEffect(() => {
    void refreshProfiles();
  }, [refreshProfiles]);

  useEffect(() => {
    void refresh();
    if (!channelId) {
      return undefined;
    }
    const interval = window.setInterval(
      () => void refresh(),
      DEFAULT_REFRESH_MS,
    );
    return () => window.clearInterval(interval);
  }, [channelId, refresh]);

  useEffect(() => {
    if (!channelId) {
      return undefined;
    }
    const unsubscribe = subscribe(
      (message) => {
        if (
          message.type === "channel_environment_changed" &&
          message.payload.channel_id === channelId
        ) {
          void refresh();
        }
      },
      { topic: "internalEventTopic" },
    );
    return () => unsubscribe();
  }, [channelId, refresh, subscribe]);

  const postAction = useCallback(
    // No "retain": the node `/retain` route was a hibernate alias, and the UI
    // control that called it is gone (hibernate is the one honest name for
    // "keep all state, release compute").
    async (action: "archive" | "restart" | "start") => {
      if (!channelId) {
        return;
      }
      const response = await fetch(
        `/user/channels/${channelId}/environment/${action}`,
        { credentials: "include", method: "POST" },
      );
      if (!response.ok) {
        throw await responseError(response);
      }
      await refresh();
    },
    [channelId, refresh],
  );
  const deleteEnvironment = useCallback(async () => {
    if (!channelId) {
      return;
    }
    const response = await fetch(`/user/channels/${channelId}/environment`, {
      credentials: "include",
      method: "DELETE",
    });
    if (!response.ok) {
      throw await responseError(response);
    }
    await refresh();
  }, [channelId, refresh]);

  return useMemo(
    () => ({
      archive: async () => await postAction("archive"),
      attach: async (values) => {
        if (!channelId) {
          return;
        }
        // Omit egressPolicy: the server request schema defaults it to
        // { mode: "deny" }. (The form exposes no egress control.)
        const response = await fetch(
          `/user/channels/${channelId}/environment`,
          {
            body: JSON.stringify(values),
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            method: "POST",
          },
        );
        if (!response.ok) {
          throw await responseError(response);
        }
        await refresh();
      },
      detach: deleteEnvironment,
      environment: state.environment,
      error: state.error,
      launchPreview: async (publicPort) => {
        if (!channelId) {
          throw new Error("No channel selected");
        }
        const response = await fetch(
          `/user/channels/${channelId}/environment/previews/${publicPort}/launch`,
          { credentials: "include", method: "POST" },
        );
        if (!response.ok) {
          throw await responseError(response);
        }
        return dashboardLaunchChannelEnvironmentPreviewResponseSchema.parse(
          await response.json(),
        ).url;
      },
      loading: state.loading,
      patch: async (values, options) => {
        if (!channelId) {
          throw new Error("No channel selected");
        }
        // Omit egressPolicy: patch leaves the existing policy unchanged.
        const response = await fetch(
          `/user/channels/${channelId}/environment`,
          {
            body: JSON.stringify({
              ...values,
              ...(options?.confirmRestart ? { confirmRestart: true } : {}),
            }),
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            method: "PATCH",
          },
        );
        const raw = await response.json().catch(() => null);
        if (isRecord(raw) && isRecord(raw.applied)) {
          const parsed = dashboardChannelEnvironmentSchema.safeParse(
            raw.applied,
          );
          if (parsed.success) {
            setState({ environment: parsed.data, error: null, loading: false });
          }
        }
        if (!response.ok) {
          if (
            response.status === 409 &&
            isRecord(raw) &&
            raw.error === "ENV_RESTART_IN_PROGRESS"
          ) {
            throw new Error("ENV_RESTART_IN_PROGRESS");
          }
          throw new Error(formatHttpError(response, raw));
        }
        return dashboardPatchChannelEnvironmentResponseSchema.parse(raw);
      },
      profiles: profilesState.profiles,
      profilesError: profilesState.error,
      profilesLoading: profilesState.loading,
      refresh,
      refreshProfiles,
      restart: async () => await postAction("restart"),
      start: async () => await postAction("start"),
    }),
    [
      channelId,
      deleteEnvironment,
      postAction,
      profilesState,
      refresh,
      refreshProfiles,
      state,
    ],
  );
}

/** Structured capacity/storage payload a resume was refused with — carried on
 *  a typed error so the UI can render it as a modal instead of a flat string. */
export type ResumeUnavailableRefusal = z.infer<
  typeof nodeChannelEnvironmentResumeUnavailableResponseSchema
>;

/** Thrown when a resume/start is refused for capacity reasons. Preserves the
 *  parsed refusal (compute deficit + held storage + suggested actions) while
 *  still carrying a human-readable message for logging/fallback. */
export class ResumeUnavailableError extends Error {
  readonly refusal: ResumeUnavailableRefusal;
  constructor(refusal: ResumeUnavailableRefusal, message: string) {
    super(message);
    this.name = "ResumeUnavailableError";
    this.refusal = refusal;
  }
}

export type ChannelEnvironmentLifecycleDiagnostic = z.infer<
  typeof nodeChannelEnvironmentLifecycleErrorResponseSchema
>;

export class ChannelEnvironmentLifecycleError extends Error {
  readonly diagnostic: ChannelEnvironmentLifecycleDiagnostic;

  constructor(
    diagnostic: ChannelEnvironmentLifecycleDiagnostic,
    message: string,
  ) {
    super(message);
    this.diagnostic = diagnostic;
    this.name = "ChannelEnvironmentLifecycleError";
  }
}

export async function responseError(response: Response): Promise<Error> {
  const text = await response.text().catch(() => "");
  let payload: unknown = text;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      // Non-JSON body; keep raw text.
    }
  }
  if (isRecord(payload)) {
    const resumeUnavailable =
      nodeChannelEnvironmentResumeUnavailableResponseSchema.safeParse(payload);
    if (resumeUnavailable.success) {
      return new ResumeUnavailableError(
        resumeUnavailable.data,
        formatHttpError(response, payload),
      );
    }
    const lifecycle =
      nodeChannelEnvironmentLifecycleErrorResponseSchema.safeParse(payload);
    if (lifecycle.success) {
      return new ChannelEnvironmentLifecycleError(
        lifecycle.data,
        formatLifecycleDiagnostic(lifecycle.data),
      );
    }
  }
  return new Error(formatHttpError(response, payload));
}

function formatHttpError(response: Response, payload: unknown): string {
  if (isRecord(payload)) {
    const resumeUnavailable =
      nodeChannelEnvironmentResumeUnavailableResponseSchema.safeParse(payload);
    if (resumeUnavailable.success) {
      const { available, deficit, requested, storageHeld, suggestedActions } =
        resumeUnavailable.data;
      const actions = suggestedActions
        .map((action) => {
          switch (action) {
            case "contact_support": {
              return "contact support";
            }
            case "downscale_cpu_ram": {
              return "downscale CPU/RAM";
            }
            case "retry": {
              return "retry";
            }
            default: {
              throw assertUnreachable(action);
            }
          }
        })
        .join(" or ");
      return [
        `Resume refused: requested ${requested.cpuMilli}m CPU, ${requested.ramMb} MiB RAM, ${requested.liveEnvironments} live slot; available ${available.cpuMilli}m CPU, ${available.ramMb} MiB RAM, ${available.liveEnvironments} live slot; deficit ${deficit.cpuMilli}m CPU, ${deficit.ramMb} MiB RAM, ${deficit.liveEnvironments} live slot.`,
        `Storage remains held: workspace ${storageHeld.workspaceMb} MiB, rootfs ${storageHeld.rootfsMb} MiB, runtime ${storageHeld.runtimeMb} MiB, hibernation archive ${storageHeld.hibernateArchiveMb} MiB, committed image ${storageHeld.committedImageMb} MiB.`,
        `Suggested action: ${actions}.`,
      ].join(" ");
    }
    const lifecycle =
      nodeChannelEnvironmentLifecycleErrorResponseSchema.safeParse(payload);
    if (lifecycle.success) {
      return formatLifecycleDiagnostic(lifecycle.data);
    }
    const previewPublicConfigReason =
      dashboardPreviewPublicConfigReasonSchema.safeParse(
        payload.previewPublicConfigReason,
      );
    if (previewPublicConfigReason.success) {
      return [
        firstString(payload.message) ??
          previewPublicConfigReasonDetail(previewPublicConfigReason.data),
        firstString(payload.action),
      ]
        .filter((part) => part !== null)
        .join(" ");
    }
    for (const key of ["message", "error", "detail"] as const) {
      const value = payload[key];
      if (typeof value === "string" && value.trim().length > 0) {
        return value;
      }
    }
  }
  if (typeof payload === "string" && payload.trim().length > 0) {
    return payload.slice(0, 240);
  }
  return `HTTP ${response.status}`;
}

function formatLifecycleDiagnostic(
  diagnostic: ChannelEnvironmentLifecycleDiagnostic,
): string {
  return [
    diagnostic.message ?? "Workspace lifecycle operation failed.",
    diagnostic.reason ? `Reason: ${diagnostic.reason}.` : null,
    diagnostic.phase ? `Phase: ${diagnostic.phase}.` : null,
    diagnostic.operation_id ? `Operation: ${diagnostic.operation_id}.` : null,
  ]
    .filter(hasValue)
    .join(" ");
}

export function previewPublicConfigReasonLabel(
  reason: PreviewPublicConfigReason,
): string {
  switch (reason) {
    case "host_missing": {
      return "Preview unconfigured";
    }
    case "invalid_url": {
      return "Preview URL invalid";
    }
    case "ok": {
      return "Public preview launchable";
    }
    case "protocol_defaulted": {
      return "Public preview launchable · protocol defaulted";
    }
    default: {
      throw assertUnreachable(reason);
    }
  }
}

export function previewPublicConfigReasonDetail(
  reason: PreviewPublicConfigReason,
): string {
  switch (reason) {
    case "host_missing": {
      return "Set ARTI_PREVIEW_PUBLIC_HOST for this deployment before launching previews.";
    }
    case "invalid_url": {
      return "Set ARTI_PREVIEW_PUBLIC_HOST and ARTI_PREVIEW_PUBLIC_PROTOCOL to a valid public preview origin.";
    }
    case "ok": {
      return "Workspace preview ports can mint public preview URLs.";
    }
    case "protocol_defaulted": {
      return "ARTI_PREVIEW_PUBLIC_PROTOCOL is not set, so public preview URLs use the default http protocol.";
    }
    default: {
      throw assertUnreachable(reason);
    }
  }
}

function firstString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}
